import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LanguageToggle } from '../../locale/components/LanguageToggle';
import { BranchSelector } from '../../branches/components/BranchSelector';
import { SupportIcon } from '../../support/components/SupportIcon';
import { FlowPageBackLink } from '../FlowPageBackLink';
import primitives from '../../../styles/primitives.module.css';
import styles from './TopNavBar.module.css';

const ROLE_KEYS = {
  STAFF: 'roleStaff',
  MANAGER: 'roleManager',
  ADMIN: 'roleAdmin',
  SUPERUSER: 'roleSuperuser',
};

function initialsFromUsername(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

export const TopNavBar = ({
  username,
  role,
  token,
  activeBranch,
  accessibleBranchCount,
  onBranchSwitch,
  onLogout,
  t,
  locale,
  onLocaleChange,
  title,
  onMenuToggle,
  isMobileMenuOpen,
  organization,
}) => {
  const isRtl = locale === 'fa';
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapRef = useRef(null);
  const menuId = useId();
  const brandName =
    organization && typeof organization.name === 'string' && organization.name.trim()
      ? organization.name.trim()
      : t('appName');
  const logoSrc =
    organization && typeof organization.logo_url === 'string' && organization.logo_url.trim()
      ? organization.logo_url.trim()
      : null;
  const [brandLogoOk, setBrandLogoOk] = useState(Boolean(logoSrc));

  useEffect(() => {
    setBrandLogoOk(Boolean(logoSrc));
  }, [logoSrc]);

  const roleLabel = useMemo(() => {
    const key = ROLE_KEYS[role];
    return key ? t(key) : role;
  }, [role, t]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onDocPointerDown = (e) => {
      const el = menuWrapRef.current;
      if (el && !el.contains(e.target)) {
        closeMenu();
      }
    };

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeMenu();
    };

    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, closeMenu]);

  const showDashboardBack =
    location.pathname === '/entry' || location.pathname === '/exit';
  const dashboardBackAriaLabel =
    location.pathname === '/exit'
      ? t('exitPageBackToDashboard')
      : t('entryPageBackToDashboard');

  const handleLogout = () => {
    closeMenu();
    onLogout();
  };

  return (
    <header
      className={`${styles.topbar} ${isRtl ? styles.topbarRtl : ''}`.trim()}
    >
      <div className={styles.titleRow}>
        {showDashboardBack ? (
          <FlowPageBackLink ariaLabel={dashboardBackAriaLabel} variant="toolbar" />
        ) : null}
        {onMenuToggle != null ? (
          <button
            type="button"
            className={`${primitives.ghostButton} ${styles.menuButton}`}
            aria-label={t(isMobileMenuOpen ? 'closeMenu' : 'openMenu')}
            onClick={onMenuToggle}
          >
            <span className={styles.menuIcon} aria-hidden>
              <span />
              <span />
              <span />
            </span>
          </button>
        ) : null}
        <div className={styles.brand}>
          <div className={styles.brandRow}>
            {logoSrc && brandLogoOk ? (
              <img
                src={logoSrc}
                alt=""
                className={styles.brandLogo}
                onError={() => setBrandLogoOk(false)}
              />
            ) : null}
            <div className={styles.brandText}>
              <span className={styles.eyebrow}>{brandName}</span>
              <span className={styles.title}>{title || t('navDashboard')}</span>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.actions}>
        <SupportIcon token={token} t={t} />
        {accessibleBranchCount > 1 ? (
          <BranchSelector
            token={token}
            t={t}
            variant="dropdown"
            activeBranch={activeBranch}
            onSelect={onBranchSwitch}
          />
        ) : null}
        <div className={styles.userMenuWrap} ref={menuWrapRef}>
          <button
            type="button"
            className={styles.userMenuTrigger}
            aria-expanded={menuOpen}
            aria-haspopup="true"
            aria-controls={menuId}
            aria-label={t('navAccountMenu')}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className={styles.userAvatar} aria-hidden>
              {initialsFromUsername(username)}
            </span>
            <span className={`${styles.chevron} ${menuOpen ? styles.chevronOpen : ''}`} aria-hidden />
          </button>
          {menuOpen ? (
            <div id={menuId} className={styles.userMenuPanel}>
              <div className={styles.userMenuIdentity}>
                <span className={styles.userMenuName}>{username || '—'}</span>
                <span className={styles.userMenuRole}>({roleLabel})</span>
              </div>
              <button
                type="button"
                className={styles.userMenuLogout}
                onClick={handleLogout}
              >
                {t('logout')}
              </button>
            </div>
          ) : null}
        </div>
        <LanguageToggle locale={locale} onChange={onLocaleChange} />
      </div>
    </header>
  );
};
